import router from 'next/router'
import { ChangeEventHandler, RefObject, useState } from 'react'
import { Button, Container, Dropdown, Form, Navbar } from 'react-bootstrap'
import {
  ControllerRenderProps,
  FieldError,
  UseFieldArrayReturn,
} from 'react-hook-form'
import { BsThreeDotsVertical } from 'react-icons/bs'
import { IoChevronBack } from 'react-icons/io5'
import { ArticleDraft, deleteDoc } from '../../../firebase'
import { useMobileKeyboardStatus } from '../../../hooks'
import { ArticleDraftFields } from '../../../hooks/forms/useArticleDraftForm'
import { PromiseVoid } from '../../../types'
import { HELP_NOTION_LINKS } from '../../../utils'
import {
  EditorTypeChangeDropdownItems,
  EditorTypeName,
} from '../../editor_addons/editor_type_changer'
import { WindowMode } from '../../mathdown/editor/types'
import { usePopupMessage } from '../../messenger'
import { PremiumPlanPaymentPopup } from '../../subscription/PremiumPlanPaymentPopup'
import { CollaborativeSettingWithoutModal } from '../CollaborativeSetting'
import { LimitedAuthorizationSetting } from '../LimitedAuthorizationSetting'

type Props = {
  draft: ArticleDraft
  headerRef: RefObject<HTMLDivElement>
  headerHeight: number

  title: string
  onChangeTitle: ChangeEventHandler<HTMLInputElement>
  saveTitleChange: () => PromiseVoid

  onSave: () => PromiseVoid

  editorType: EditorTypeName
  setEditorType: (editorType: EditorTypeName) => void

  sharedPath: string

  saveEditabilityChange: () => PromiseVoid
  editableUidsFieldArray: UseFieldArrayReturn<
    ArticleDraftFields,
    'editable_uids',
    'id'
  >
  limitationTypeField: ControllerRenderProps<
    ArticleDraftFields,
    'limitation_type'
  >

  errors: {
    title?: FieldError
  }

  windowMode: WindowMode
  setWindowMode: (windowMode: WindowMode) => void
}

export const MobileHeader = ({
  onSave: handleSave,
  draft,
  sharedPath,
  headerRef,
  headerHeight,
  title,
  onChangeTitle: handleTitleChange,
  saveTitleChange,
  editorType,
  setEditorType,
  saveEditabilityChange,
  editableUidsFieldArray,
  limitationTypeField,
  windowMode,
  setWindowMode,
  errors,
}: Props) => {
  const [isTitleFocused, setIsTitleFocused] = useState(false)
  const keyboardStatus = useMobileKeyboardStatus()
  const { setPopupMessage } = usePopupMessage()
  const [showPlanPopup, setShowPlanPopup] = useState(false)
  const [showCollaborativeSettingModal, setShowCollaborativeSettingModal] =
    useState(false)

  return (
    <>
      {(isTitleFocused || keyboardStatus === 'close') && (
        <>
          <Navbar
            ref={headerRef}
            expand="md"
            variant="dark"
            className="flex-shrink-0 shadow-sm px-1 bg-primary-dark fixed-top d-flex flex-nowrap align-items-center justify-content-start d-md-none gap-2"
            style={{ height: 50 }} // ヘッダーの高さは中の要素の高さの影響を受けないように固定値にした
          >
            {(!isTitleFocused || keyboardStatus === 'close') && (
              <Button
                variant="link"
                className="text-white fs-4"
                onClick={async () => {
                  await handleSave()
                  await router.push(`/article_drafts`)
                }}
              >
                <IoChevronBack />
              </Button>
            )}
            <Form.Control
              onChange={handleTitleChange}
              value={title}
              onFocus={() => {
                setIsTitleFocused(true)
              }}
              onBlur={async () => {
                setIsTitleFocused(false)
                await saveTitleChange()
              }}
              isInvalid={!!errors.title}
              placeholder="タイトルを入力"
              className="d-inline flex-fill text-light text-truncate"
              size="sm"
              plaintext
            />
            {(!isTitleFocused || keyboardStatus === 'close') && (
              <>
                <Dropdown align={'end'}>
                  <Dropdown.Toggle
                    variant="link"
                    className="text-light fs-5 dropdown-toggle-after-none me-2"
                  >
                    <BsThreeDotsVertical />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <EditorTypeChangeDropdownItems
                      editorType={editorType}
                      onChangeEditorType={setEditorType}
                      windowMode={windowMode}
                      onChangeWindowMode={setWindowMode}
                    />
                    <Dropdown.Divider />
                    <Dropdown.Item
                      onClick={async () => {
                        await router.push(
                          `/article_drafts/${draft.id}/publication`
                        )
                      }}
                    >
                      投稿に進む
                    </Dropdown.Item>
                    <CollaborativeSettingWithoutModal
                      as="dropdown_item"
                      setShowPlanPopup={setShowPlanPopup}
                      setShowCollaborativeSettingModal={
                        setShowCollaborativeSettingModal
                      }
                    />
                    <Dropdown.Item
                      className="text-danger"
                      onClick={async () => {
                        const windowConfirm =
                          window.confirm('本当に削除しますか？')
                        if (!windowConfirm) return
                        await deleteDoc(draft.ref)
                        setPopupMessage('下書きを削除しました。', 'danger')
                        await router.push(`/article_drafts`)
                      }}
                    >
                      下書きを削除
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </>
            )}
          </Navbar>
          <Container
            className="flex-shrink-0 d-print-none d-md-none"
            style={{ height: headerHeight }}
            fluid
          />
        </>
      )}
      <PremiumPlanPaymentPopup
        show={showPlanPopup}
        onHide={() => setShowPlanPopup(false)}
      />
      <LimitedAuthorizationSetting
        title="共同編集の権限設定"
        help={HELP_NOTION_LINKS.COLLABORATIVE_EDIT}
        helpEvent="tap_collaborative_edit_help"
        sharedPath={sharedPath}
        authorizedUidsFieldArray={editableUidsFieldArray}
        limitationTypeField={limitationTypeField}
        show={showCollaborativeSettingModal}
        onHide={async () => {
          setShowCollaborativeSettingModal(false)
          await saveEditabilityChange()
        }}
      />
    </>
  )
}
